import type { UnitCatalog } from '@/hooks/useUnits';

interface Props {
  catalog: UnitCatalog;
  /** The item's stock unit code (Kg) */
  stockUnit?: string | null;
  /** Unit the line is entered in; null / undefined = the stock unit */
  value?: string | null;
  /** Called with the chosen unit id, or null when the stock unit is chosen */
  onChange: (unitId: string | null) => void;
  disabled?: boolean;
  className?: string;
}

/**
 * Unit picker for a transaction line: the units of the item's stock-unit category (Kg, g, Ton).
 * Shows the plain unit when there is nothing to choose from.
 */
export default function LineUnitSelect({ catalog, stockUnit, value, onChange, disabled, className }: Props) {
  const options = catalog.optionsFor(stockUnit);
  const stock = catalog.byCode(stockUnit);
  const current = catalog.byId(value) ?? stock;

  if (options.length <= 1 || !stock) {
    return <span className={className ?? 'text-sm text-slate-600'}>{current?.shortName ?? stockUnit ?? ''}</span>;
  }

  return (
    <select
      value={current?.id ?? ''}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value === stock.id ? null : e.target.value)}
      title={`Stock unit: ${stock.shortName}`}
      className={
        className ??
        'h-8 px-1.5 text-sm bg-white border border-[#e2e8f0] rounded-md focus:outline-none focus:border-[#4f46e5] cursor-pointer disabled:bg-slate-50 disabled:cursor-not-allowed'
      }
    >
      {options.map((u) => (
        <option key={u.id} value={u.id}>
          {u.shortName}
        </option>
      ))}
    </select>
  );
}
